import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config.js";
import VehicleImagePlaceholder from "./VehicleImagePlaceholder";
import { useUser } from "../../pages/login/UserContextPersistent";

/** Latest VehicleImage document URL (same pattern as VehicleDetailsOverlay). */
function getVehicleImageUrl(vehicle) {
  if (!vehicle?.documents?.length) return null;
  const vehicleImages = vehicle.documents.filter((doc) => doc.name === "VehicleImage");
  if (!vehicleImages.length) return null;
  vehicleImages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const doc = vehicleImages[0];
  return doc?.location
    ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(doc.location)}`
    : null;
}

function getOrgId() {
  try { return JSON.parse(localStorage.getItem("userState") || "{}").user?.organizationId; }
  catch { return null; }
}

const sectionHeader = (label) => (
  <div style={{
    background: "#95A0B3", color: "#fff", padding: "8px 12px",
    fontWeight: 500, fontSize: 16, borderRadius: 4, marginBottom: 4,
  }}>{label}</div>
);

const inputStyle = {
  width: "100%",
  border: "2px solid #183059",
  borderRadius: 16,
  padding: "16px 24px",
  fontSize: 14,
  boxSizing: "border-box",
  height: 51,
  background: "#fff",
};

const label = (text, required = false) => (
  <div style={{ fontSize: 16, color: "#1F242E", fontWeight: 600, marginBottom: 6 }}>
    {text}
    {required ? <span style={{ color: "#D32F2F", marginLeft: 4 }}>*</span> : null}
  </div>
);

/** Prisma VehicleType enum values — must match option values so the select stays controlled */
const VEHICLE_TYPE_OPTIONS = [
  { value: "CAR", label: "Car" },
  { value: "TRUCK", label: "Truck" },
  { value: "VAN", label: "Van" },
  { value: "BUS", label: "Bus" },
  { value: "BIKE", label: "Bike" },
  { value: "OTHER", label: "Other" },
];

export default function CreateEntryExitForm({ organizationId: propOrgId, onClose, onCreated }) {
  const { organizationInactive } = useUser();
  const organizationId = propOrgId || getOrgId();
  const [vehicles, setVehicles] = useState([]);
  const [gates, setGates] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);

  const [selectExisting, setSelectExisting] = useState(true);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleImageFailed, setVehicleImageFailed] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState("");
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    vehicleName: "", vehicleType: "", vehicleRegNo: "",
    entryGateId: "", entryDate: new Date().toISOString().slice(0, 10), entryTime: "",
    exitGateId: "", exitDate: "", exitTime: "",
    driver: "", driverPhone: "", comments: "",
    siteId: "",
  });

  const normalizeList = (raw, fallbackKey) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.[fallbackKey])) return raw[fallbackKey];
    return [];
  };

  const loadGates = async (siteId) => {
    if (!organizationId) return;

    try {
      // Prefer site-gates when a vehicle/site is selected.
      if (siteId) {
        const siteResp = await axios.get(`${config.baseURL}/stop/gates/site/${siteId}`, {
          withCredentials: true,
        });
        const siteGates = normalizeList(siteResp.data, "gates");
        if (siteGates.length > 0) {
          setGates(siteGates);
          return;
        }
      }

      // Fallback to organization-level Gate stops (still Gate-only, not all stops).
      const orgResp = await axios.get(`${config.baseURL}/stop/gates`, {
        withCredentials: true,
        params: { organizationId: String(organizationId) },
      });
      const orgGates = normalizeList(orgResp.data, "gates");
      setGates(orgGates);
    } catch {
      setGates([]);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    axios
      .get(`${config.baseURL}/vehicle/organization`, {
        withCredentials: true,
        params: { organizationId: String(organizationId) },
      })
      .then((r) => {
        const raw = r.data;
        const list = Array.isArray(raw) ? raw : raw?.vehicles || [];
        setVehicles(list);
      })
      .catch(() => setVehicles([]));
    axios.get(`${config.baseURL}/site/organization?organizationId=${organizationId}`, { withCredentials: true })
      .then(r => setSites(r.data?.sites || r.data || [])).catch(() => {});
    loadGates(form.siteId || "");
  }, [organizationId]);

  // Refresh gates when site changes
  useEffect(() => {
    if (!organizationId) return;
    loadGates(form.siteId || "");
  }, [form.siteId, organizationId]);

  const vehicleImageUrl = selectedVehicle ? getVehicleImageUrl(selectedVehicle) : null;

  useEffect(() => {
    setVehicleImageFailed(false);
  }, [selectedVehicleId]);

  useEffect(() => {
    return () => {
      if (pendingImagePreview) {
        URL.revokeObjectURL(pendingImagePreview);
      }
    };
  }, [pendingImagePreview]);

  useEffect(() => {
    if (!selectExisting) {
      setSelectedVehicleId("");
      setSelectedVehicle(null);
      setForm((p) => ({
        ...p,
        vehicleName: "",
        vehicleType: "",
        vehicleRegNo: "",
        driver: "",
        driverPhone: "",
        siteId: "",
        entryGateId: "",
        exitGateId: "",
      }));
    }
  }, [selectExisting]);

  const handleVehicleSelect = (id) => {
    setSelectedVehicleId(id);
    const v = vehicles.find((x) => String(x.id) === String(id));
    setSelectedVehicle(v || null);
    if (v) {
      setForm(p => ({
        ...p,
        vehicleName: v.name || "",
        vehicleType: v.VehicleType != null && v.VehicleType !== "" ? String(v.VehicleType) : "",
        vehicleRegNo: v.VehicleNumber || "",
        driver: v.driver ? `${v.driver.first_name} ${v.driver.last_name}` : "",
        driverPhone: v.driver?.phone_number || "",
        siteId: v.siteId ? String(v.siteId) : "",
        entryGateId: "",
        exitGateId: "",
      }));
    }
  };

  const handleImageContainerClick = () => {
    fileInputRef.current?.click();
  };

  const uploadVehicleImage = async (vehicleId, file) => {
    const formData = new FormData();
    formData.append("image", file);
    setUploadingImage(true);
    try {
      const res = await axios.post(
        `${config.baseURL}/upload/image?name=${encodeURIComponent(
          "VehicleImage"
        )}&associateEntityId=${encodeURIComponent(
          vehicleId
        )}&associatedWith=Vehicle&type=profile`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const newDoc = {
        name: "VehicleImage",
        location: res.data?.location,
        createdAt: new Date().toISOString(),
      };
      setSelectedVehicle((prev) =>
        prev
          ? {
              ...prev,
              documents: [...(prev.documents || []), newDoc],
            }
          : prev
      );
      setVehicleImageFailed(false);
      return true;
    } catch {
      return false;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVehicleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    // Allow selecting the same file again.
    e.target.value = "";
    if (!file) return;

    // Existing vehicle mode: upload immediately.
    if (selectedVehicleId) {
      const ok = await uploadVehicleImage(selectedVehicleId, file);
      if (ok) {
        toast.success("Vehicle image uploaded");
      } else {
        toast.error("Failed to upload vehicle image");
      }
      return;
    }

    // New vehicle mode: keep image to upload right after vehicle creation.
    setPendingImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setPendingImagePreview(previewUrl);
    setVehicleImageFailed(false);
    toast.info("Image selected. It will be uploaded when you save the record.");
  };

  const handleSubmit = async () => {
    if (organizationInactive) {
      toast.error(
        "Your organization is inactive. Contact the RouteEye admin to make changes.",
      );
      return;
    }
    if (!form.entryDate) {
      toast.error("Please select entry date");
      return;
    }
    setSaving(true);
    try {
      let vehicleId = selectedVehicleId;
      const requiredChecks = [
        ["Name", form.vehicleName],
        ["Type", form.vehicleType],
        ["Reg. No.", form.vehicleRegNo],
        ["Entry Gate", form.entryGateId],
        ["Entry Date", form.entryDate],
        ["Entry Time", form.entryTime],
        ["Exit Gate", form.exitGateId],
        ["Exit Date", form.exitDate],
        ["Exit Time", form.exitTime],
      ];
      if (selectExisting) {
        requiredChecks.unshift(["Vehicle", selectedVehicleId]);
      }
      const missing = requiredChecks
        .filter(([, value]) => String(value || "").trim() === "")
        .map(([field]) => field);
      if (missing.length > 0) {
        toast.error(`Please fill required fields: ${missing.join(", ")}`);
        setSaving(false);
        return;
      }

      const entryDateTime = form.entryTime
        ? new Date(`${form.entryDate}T${form.entryTime}`)
        : new Date(`${form.entryDate}T00:00`);
      const exitDateTime = form.exitDate
        ? (form.exitTime ? new Date(`${form.exitDate}T${form.exitTime}`) : new Date(`${form.exitDate}T00:00`))
        : null;

      if (exitDateTime && exitDateTime < entryDateTime) {
        toast.error("Exit date/time must be greater than or equal to entry date/time");
        setSaving(false);
        return;
      }

      if (!selectExisting) {
        if (!form.vehicleName?.trim() || !form.vehicleRegNo?.trim()) {
          toast.error("Please enter vehicle name and registration number");
          setSaving(false);
          return;
        }

        const normalizedReg = form.vehicleRegNo.trim().toLowerCase();
        const normalizedName = form.vehicleName.trim().toLowerCase();
        const alreadyExists = vehicles.some((v) => {
          const regMatch =
            String(v.VehicleNumber || "").trim().toLowerCase() === normalizedReg;
          const nameMatch =
            String(v.name || "").trim().toLowerCase() === normalizedName;
          return regMatch || (nameMatch && regMatch);
        });
        if (alreadyExists) {
          toast.error(
            "Select existing vehicle. This vehicle is already present."
          );
          setSaving(false);
          return;
        }

        const createVehiclePayload = {
          name: form.vehicleName.trim(),
          VehicleNumber: form.vehicleRegNo.trim(),
          organizationId: Number(organizationId),
          description: "Created from manual vehicle entry/exit form",
          ...(form.vehicleType?.trim() ? { VehicleType: form.vehicleType.trim() } : {}),
        };
        const createVehicleResp = await axios.post(
          `${config.baseURL}/vehicle`,
          createVehiclePayload,
          { withCredentials: true }
        );
        vehicleId = String(createVehicleResp?.data?.vehicle?.id || "");
        if (!vehicleId) {
          toast.error("Vehicle created but could not resolve vehicle id");
          setSaving(false);
          return;
        }
      }

      if (!vehicleId) {
        toast.error("Please select a vehicle");
        setSaving(false);
        return;
      }

      const vehicle = vehicles.find(v => String(v.id) === String(vehicleId));
      const entryGate = gates.find((g) => String(g.id) === String(form.entryGateId));
      const exitGate = gates.find((g) => String(g.id) === String(form.exitGateId));
      let siteId = form.siteId || vehicle?.siteId || entryGate?.siteId || exitGate?.siteId;

      // If list payload doesn't include siteId for gates, resolve from gate details.
      if (!siteId) {
        const selectedGateId = form.entryGateId || form.exitGateId;
        if (selectedGateId) {
          try {
            const stopResp = await axios.get(
              `${config.baseURL}/stop/id?id=${selectedGateId}`,
              { withCredentials: true }
            );
            siteId = stopResp?.data?.siteId || stopResp?.data?.stop?.siteId || "";
          } catch {
            // keep fallback error below
          }
        }
      }

      if (!siteId) {
        toast.error("Please select at least one gate mapped to a site.");
        setSaving(false);
        return;
      }

      if (pendingImageFile && !selectedVehicleId) {
        const ok = await uploadVehicleImage(vehicleId, pendingImageFile);
        if (!ok) {
          toast.error("Vehicle created, but image upload failed");
        } else {
          toast.success("Vehicle image uploaded");
        }
      }

      await axios.post(`${config.baseURL}/vehicle-log/manual`, {
        vehicleId,
        siteId,
        entryGateId: form.entryGateId || null,
        exitGateId: form.exitGateId || null,
        entryDateTime,
        exitDateTime,
        ...(form.vehicleType?.trim() ? { vehicleType: form.vehicleType.trim() } : {}),
        comments: form.comments || null,
      }, { withCredentials: true });

      toast.success("Record created");
      setPendingImageFile(null);
      setPendingImagePreview("");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: "#E8EAEE",
      borderRadius: 10,
      width: "100%",
      padding: 16,
      border: "1px solid #C8CBD0",
    }}>
        <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "250px repeat(4, minmax(220px, 1fr))",
            gap: 16,
            alignItems: "start",
            minWidth: 1200,
          }}
        >
          {/* Vehicle image placeholder */}
          <div
            style={{
              width: "100%",
              minHeight: 360,
              flexShrink: 0,
              border: "2px dashed #183059",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#E8EAEE",
              padding: 12,
              cursor: "pointer",
              opacity: uploadingImage ? 0.7 : 1,
            }}
            onClick={handleImageContainerClick}
            title={
              selectExisting
                ? selectedVehicleId
                  ? "Click to upload vehicle image"
                  : "Select a vehicle first"
                : "Click to choose image (it will upload after Save)"
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleVehicleImageUpload}
            />
            <div style={{ width: "100%", height: 220, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#F4F6FA", overflow: "hidden" }}>
              {vehicleImageUrl && !vehicleImageFailed ? (
                <img
                  src={vehicleImageUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={() => setVehicleImageFailed(true)}
                />
              ) : pendingImagePreview ? (
                <img
                  src={pendingImagePreview}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <VehicleImagePlaceholder width={100} height={135} />
                  <span style={{ fontSize: 12, color: "#647490" }}>
                    {uploadingImage
                      ? "Uploading image..."
                      : "Click to upload vehicle image"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Details */}
          <div style={{ minWidth: 220 }}>
            {sectionHeader("Vehicle Details")}
            <div style={{ padding: 0 }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 16, cursor: "pointer", color: "#20A8E0", fontWeight: 500 }}>
                  <input type="checkbox" checked={selectExisting} onChange={e => setSelectExisting(e.target.checked)} />
                  Select Existing Vehicle
                </label>
              </div>
              <div style={{ marginBottom: 6 }}>
                {label("Vehicle", selectExisting)}
                <select
                  value={selectedVehicleId}
                  onChange={e => handleVehicleSelect(e.target.value)}
                  style={{
                    ...inputStyle,
                    background: selectExisting ? "#fff" : "#F4F6FA",
                    cursor: selectExisting ? "pointer" : "not-allowed",
                  }}
                  disabled={!selectExisting}
                >
                  <option value="">
                    {selectExisting ? "-- Select Vehicle --" : "-- New Vehicle Mode --"}
                  </option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} ({v.VehicleNumber})</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 6 }}>{label("Name", true)}<input style={inputStyle} value={form.vehicleName} onChange={e => setForm(p => ({ ...p, vehicleName: e.target.value }))} placeholder="Enter vehicle name" /></div>
              <div style={{ marginBottom: 6 }}>
                {label("Type", true)}
                <select style={inputStyle} value={form.vehicleType} onChange={e => setForm(p => ({ ...p, vehicleType: e.target.value }))}>
                  <option value="">—</option>
                  {VEHICLE_TYPE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>{label("Reg. No.", true)}<input style={inputStyle} value={form.vehicleRegNo} onChange={e => setForm(p => ({ ...p, vehicleRegNo: e.target.value }))} placeholder="Enter registration number" /></div>
            </div>
          </div>

          {/* Entry Details */}
          <div style={{ minWidth: 220 }}>
            {sectionHeader("Entry Details")}
            <div style={{ padding: 0 }}>
              <div style={{ marginBottom: 6 }}>
                {label("Gate", true)}
                <select style={inputStyle} value={form.entryGateId} onChange={e => setForm(p => ({ ...p, entryGateId: e.target.value }))}>
                  <option value=""></option>
                  {gates.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name || g.stopId || `Gate ${g.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 6 }}>{label("Date", true)}<input type="date" style={inputStyle} value={form.entryDate} onChange={e => setForm(p => ({ ...p, entryDate: e.target.value }))} /></div>
              <div>{label("Time", true)}<input type="time" style={inputStyle} value={form.entryTime} onChange={e => setForm(p => ({ ...p, entryTime: e.target.value }))} /></div>
            </div>
          </div>

          {/* Exit Details */}
          <div style={{ minWidth: 220 }}>
            {sectionHeader("Exit Details")}
            <div style={{ padding: 0 }}>
              <div style={{ marginBottom: 6 }}>
                {label("Gate", true)}
                <select style={inputStyle} value={form.exitGateId} onChange={e => setForm(p => ({ ...p, exitGateId: e.target.value }))}>
                  <option value=""></option>
                  {gates.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name || g.stopId || `Gate ${g.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 6 }}>{label("Date", true)}<input type="date" style={inputStyle} value={form.exitDate} onChange={e => setForm(p => ({ ...p, exitDate: e.target.value }))} /></div>
              <div>{label("Time", true)}<input type="time" style={inputStyle} value={form.exitTime} onChange={e => setForm(p => ({ ...p, exitTime: e.target.value }))} /></div>
            </div>
          </div>

          {/* Other Details */}
          <div style={{ minWidth: 220 }}>
            {sectionHeader("Other Details")}
            <div style={{ padding: 0 }}>
              <div style={{ marginBottom: 6 }}>
                {label("Duration")}
                <input style={{ ...inputStyle, background: "#B7BFCC", color: "#F2F2F3", border: "none" }} value="Total duration stayed" readOnly />
              </div>
              <div style={{ marginBottom: 6 }}>{label("Driver")}<input style={inputStyle} value={form.driver} onChange={e => setForm(p => ({ ...p, driver: e.target.value }))} placeholder="Driver name" /></div>
              <div>{label("Driver's Phone Number")}<input style={inputStyle} value={form.driverPhone} onChange={e => setForm(p => ({ ...p, driverPhone: e.target.value }))} placeholder="Enter driver's number" /></div>
            </div>
          </div>
        </div>
        </div>

        {/* Comment */}
        <div style={{ marginTop: 16 }}>
          {label("Add Comment")}
          <textarea
            rows={2}
            value={form.comments}
            onChange={e => setForm(p => ({ ...p, comments: e.target.value }))}
            placeholder="Enter your comment or flag any issue"
            style={{ ...inputStyle, minHeight: 80, height: "auto", resize: "vertical" }}
          />
        </div>

        {/* Bottom actions */}
        <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              border: "1px solid #ccc",
              background: "#F2F2F3",
              borderRadius: 6,
              padding: "5px 20px",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Cancel
          </button>
          {!organizationInactive && (
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: "#F9E719",
              border: "1px solid #414958",
              borderRadius: 6,
              padding: "5px 20px",
              fontWeight: 600,
              cursor: "pointer",
              opacity: saving ? 0.7 : 1,
              fontSize: 12,
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          )}
        </div>
    </div>
  );
}
